// Track a budget line: choose how big a change is worth an alert. Removal from the budget, renames,
// congressional marks and newly linked records always alert.
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, errorText } from "../api/client";
import type { BudgetDetail } from "../api/types";
import { useUI } from "../app/context";
import { Modal } from "./Modal";

const STEPS: [number, string][] = [
  [0, "Any change"],
  [5, "5% or more"],
  [10, "10% or more"],
  [25, "25% or more"],
  [50, "50% or more"],
];

export function TrackBudget({ b, open, onClose }: { b: BudgetDetail; open: boolean; onClose: () => void }) {
  const client = useQueryClient();
  const { showToast } = useUI();
  const [pct, setPct] = useState(b.tracking?.threshold_pct ?? 10);
  const [amount, setAmount] = useState(b.tracking?.min_amount ? String(b.tracking.min_amount) : "");
  const path = "/api/budget/tracked/" + encodeURIComponent(b.id);
  const done = (message: string) => {
    onClose();
    client.invalidateQueries({ queryKey: ["budget"] });
    showToast(message);
  };
  const save = async () => {
    try {
      await api(path, { method: "PUT", body: { threshold_pct: pct, min_amount: Number(amount) || 0 } });
      done(b.tracking ? "Tracking updated." : `Tracking ${b.title}. You’ll get an alert when it changes.`);
    } catch (err) {
      showToast(errorText(err));
    }
  };
  const stop = async () => {
    await api(path, { method: "DELETE" });
    done("No longer tracking it.");
  };
  return (
    <Modal open={open} id="track-budget" onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div className="dlg">
          <h2>Track {b.title}</h2>
          <p>Alert me when its funding changes by:</p>
          <div className="opts" id="tb-pct">
            {STEPS.map(([v, label]) => (
              <label key={v}>
                <input type="radio" name="pct" value={v} checked={pct === v} onChange={() => setPct(v)} />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <label className="field">
            <span>And by at least ($ millions)</span>
            <input type="number" id="tb-amount" min={0} step="0.1" placeholder="No minimum" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <small>Changes compare each budget’s request with the last one GovChew saw, and a new year’s budget with what the previous one planned.</small>
          </label>
          <p className="muted-s">Dropped from the budget, renamed, a congressional mark, or a newly linked record: always an alert.</p>
        </div>
        <div className="foot">
          {b.tracking ? (
            <button type="button" className="btn danger left" onClick={stop}>
              Stop tracking
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary">{b.tracking ? "Save" : "Track"}</button>
        </div>
      </form>
    </Modal>
  );
}
